import { useState } from "react";
import { Fingerprint, Lock, Key } from "lucide-react";
import { C, sBtn, sInp, sShell, sPanel, sGrid, sGlow } from "../styles";

/* Lock screen modes:
   - bio    (default if authMeta.hasBio)
   - pin    (fallback)
   - recover (enter 12-word phrase)
*/

export default function LockScreen({
    authMeta,
    onUnlockBio,
    onUnlockPin,
    onUnlockRecovery,
    showToast,
}) {
    const defaultMode = authMeta?.hasBio ? "bio" : "pin";
    const [mode, setMode] = useState(defaultMode);
    const [pin, setPin] = useState("");
    const [phrase, setPhrase] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showHint, setShowHint] = useState(false);

    const tryBio = async () => {
        setBusy(true);
        const ok = await onUnlockBio();
        setBusy(false);
        if (!ok) setErr("Face ID failed — try PIN instead");
    };

    const tryPin = async () => {
        setErr("");
        setBusy(true);
        const ok = await onUnlockPin(pin);
        setBusy(false);
        if (!ok) {
            setErr("Incorrect PIN");
            setPin("");
        } else {
            setPin("");
        }
    };

    const tryRecovery = async () => {
        setErr("");
        setBusy(true);
        const ok = await onUnlockRecovery(phrase);
        setBusy(false);
        if (!ok) setErr("Recovery phrase didn't match this vault");
        else setPhrase("");
    };

    return (
        <div style={sShell}>
            <div style={sGrid} />
            <div style={sGlow} />
            <div style={sPanel} data-testid="lock-panel">
                <div
                    style={{
                        fontSize: 10,
                        letterSpacing: "0.3em",
                        color: C.accent,
                        textTransform: "uppercase",
                        marginBottom: 10,
                    }}
                >
                    Hikari Labs
                </div>
                <h1
                    style={{
                        fontSize: 28,
                        fontWeight: 300,
                        color: C.text,
                        letterSpacing: "-0.02em",
                        marginBottom: 6,
                    }}
                >
                    Vault
                </h1>
                <p
                    style={{
                        fontSize: 13,
                        color: C.muted,
                        marginBottom: 26,
                    }}
                >
                    {mode === "bio" && "Unlock with Face ID or Touch ID."}
                    {mode === "pin" && "Enter your 6-digit PIN."}
                    {mode === "recover" &&
                        "Enter your 12-word recovery phrase."}
                </p>

                {mode === "bio" && (
                    <>
                        <button
                            style={sBtn("primary")}
                            onClick={tryBio}
                            disabled={busy}
                            data-testid="lock-bio-btn"
                        >
                            <Fingerprint
                                size={14}
                                style={{
                                    verticalAlign: "-2px",
                                    marginRight: 8,
                                }}
                            />
                            {busy ? "Verifying…" : "Unlock with Face ID"}
                        </button>
                        {authMeta?.hasPin && (
                            <button
                                style={sBtn("ghost")}
                                onClick={() => {
                                    setMode("pin");
                                    setErr("");
                                }}
                                data-testid="lock-use-pin-btn"
                            >
                                <Lock
                                    size={13}
                                    style={{
                                        verticalAlign: "-2px",
                                        marginRight: 6,
                                    }}
                                />
                                Use PIN instead
                            </button>
                        )}
                    </>
                )}

                {mode === "pin" && (
                    <>
                        <input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="off"
                            maxLength={12}
                            placeholder="••••••"
                            value={pin}
                            onChange={(e) =>
                                setPin(e.target.value.replace(/\D/g, ""))
                            }
                            onKeyDown={(e) => e.key === "Enter" && tryPin()}
                            style={sInp(true)}
                            autoFocus
                            data-testid="lock-pin-input"
                        />
                        {authMeta?.pinHint && (
                            <button
                                onClick={() => setShowHint(!showHint)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: C.dim,
                                    fontSize: 11,
                                    cursor: "pointer",
                                    fontFamily: C.font,
                                    marginBottom: 8,
                                }}
                                data-testid="lock-show-hint-btn"
                            >
                                {showHint
                                    ? `hint: ${authMeta.pinHint}`
                                    : "Show hint"}
                            </button>
                        )}
                        <button
                            style={sBtn("primary")}
                            onClick={tryPin}
                            disabled={busy || pin.length < 6}
                            data-testid="lock-pin-submit-btn"
                        >
                            {busy ? "Verifying…" : "Unlock"}
                        </button>
                        {authMeta?.hasBio && (
                            <button
                                style={sBtn("ghost")}
                                onClick={() => {
                                    setMode("bio");
                                    setErr("");
                                }}
                                data-testid="lock-back-to-bio-btn"
                            >
                                Use Face ID
                            </button>
                        )}
                        <button
                            style={{
                                background: "none",
                                border: "none",
                                color: C.dim,
                                fontSize: 11,
                                cursor: "pointer",
                                fontFamily: C.font,
                                marginTop: 8,
                            }}
                            onClick={() => {
                                setMode("recover");
                                setErr("");
                            }}
                            data-testid="lock-forgot-btn"
                        >
                            <Key
                                size={11}
                                style={{
                                    verticalAlign: "-1px",
                                    marginRight: 5,
                                }}
                            />
                            Forgot PIN? Use recovery phrase
                        </button>
                    </>
                )}

                {mode === "recover" && (
                    <>
                        <textarea
                            placeholder="Enter your 12-word recovery phrase, separated by spaces"
                            value={phrase}
                            onChange={(e) => setPhrase(e.target.value)}
                            rows={4}
                            style={{
                                ...sInp(),
                                minHeight: 96,
                                resize: "vertical",
                                letterSpacing: "normal",
                                textAlign: "left",
                            }}
                            autoFocus
                            data-testid="lock-recovery-input"
                        />
                        <button
                            style={sBtn("primary")}
                            onClick={tryRecovery}
                            disabled={busy || phrase.split(/\s+/).filter(Boolean).length < 12}
                            data-testid="lock-recovery-submit-btn"
                        >
                            {busy ? "Verifying…" : "Recover vault"}
                        </button>
                        <button
                            style={sBtn("ghost")}
                            onClick={() => {
                                setMode("pin");
                                setErr("");
                            }}
                            data-testid="lock-back-btn"
                        >
                            ← Back
                        </button>
                    </>
                )}

                {err && (
                    <div
                        style={{
                            color: C.danger,
                            fontSize: 12,
                            marginTop: 10,
                            textAlign: "center",
                        }}
                        data-testid="lock-error"
                    >
                        {err}
                    </div>
                )}
            </div>
        </div>
    );
}
